import { createClient } from '@supabase/supabase-js'

// Replace these with your actual Supabase project URL and anon key
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

// Validate that required environment variables are set
if (!supabaseUrl) {
  throw new Error(
    'REACT_APP_SUPABASE_URL is required. Please create a .env file in your project root and add your Supabase project URL.'
  )
}

if (!supabaseAnonKey) {
  throw new Error(
    'REACT_APP_SUPABASE_ANON_KEY is required. Please create a .env file in your project root and add your Supabase anon key.'
  )
}

console.log('Supabase URL:', supabaseUrl)

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Export individual services for convenience
export const auth = supabase.auth
export const db = supabase.from
export const storage = supabase.storage
export const functions = supabase.functions

// Helper functions for common operations
export const signUp = async (email, password, userData = {}) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: userData
    }
  })
  return { data, error }
}

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  })
  return { data, error }
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  return { error }
}

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser()
  return { user, error }
}

export const onAuthStateChange = (callback) => {
  return supabase.auth.onAuthStateChange(callback)
}

// Function to save user profile data to users table
export const saveUserProfile = async (userId, userProfileData) => {
  const { data, error } = await supabase
    .from('users')
    .insert([
      {
        id: userId,
        name: userProfileData.name,
        email: userProfileData.email,
        contact: userProfileData.contact,
        company_name: userProfileData.companyName,
        gst_no: userProfileData.gstNo,
        gst_document: userProfileData.gstDocument,
        agreement: userProfileData.agreement,
        other_doc: userProfileData.otherDoc,
        insurance_type: userProfileData.insuranceType,
        company_address: userProfileData.companyAddress,
        role: userProfileData.role || 'client',
        partner_type: userProfileData.partnerType || 'postpaid',
        wallet_balance: userProfileData.walletBalance || '',
        service: userProfileData.service || 'logistics',
        rate_list: userProfileData.rateList || null
      }
    ])
  
  return { data, error }
}

// Function to upload file to Supabase storage
export const uploadFile = async (bucketName, filePath, file) => {
  const { data, error } = await supabase.storage
    .from(bucketName)
    .upload(filePath, file)
  
  return { data, error }
}

// Function to get public URL for uploaded file
export const getFileUrl = (bucketName, filePath) => {
  const { data } = supabase.storage
    .from(bucketName)
    .getPublicUrl(filePath)
  
  return data.publicUrl
}

// User management functions for the Users list
export const getUsersByRole = async (role, service) => {
  console.log('🔍 getUsersByRole called with:', { role, service })
  
  try {
    let query = supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false })
    
    // Always filter by both role and service
    if (role && service) {
      console.log('🔍 Filtering by role and service:', { role, service })
      query = query
        .eq('role', role)
        .eq('service', service)
    }
    // If only role provided, filter by role only
    else if (role) {
      console.log('🔍 Filtering by role only:', role)
      query = query.eq('role', role)
    }
    // If no filters provided, return all users
    else {
      console.log('🔍 No filters provided, returning all users')
    }
    
    const { data, error } = await query
    
    console.log('🔍 getUsersByRole result:', { 
      data: data?.length || 0, 
      error,
      filters: { role, service }
    })
    
    if (error) {
      console.error('❌ getUsersByRole error:', error)
    }
    
    return { data, error }
  } catch (err) {
    console.error('❌ getUsersByRole exception:', err)
    return { data: null, error: err }
  }
}

export const getUsersByRoles = async (roles, service = 'logistics') => {
  console.log('🔍 getUsersByRoles called with:', { roles, service })
  
  try {
    let query = supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false })
    
    // If roles is an array and service is provided, filter by both
    if (Array.isArray(roles) && roles.length > 0 && service) {
      console.log('🔍 Filtering by roles array and service:', { roles, service })
      query = query
        .in('role', roles)
        .eq('service', service)
    }
    // If roles is a single string and matches service, show all users in that category
    else if (typeof roles === 'string' && roles === service) {
      console.log('🔍 Roles and service match, showing all users in category:', roles)
      query = query.eq('role', roles)
    }
    // If roles is a single string but doesn't match service, filter by both
    else if (typeof roles === 'string' && service) {
      console.log('🔍 Filtering by single role and service:', { roles, service })
      query = query
        .eq('role', roles)
        .eq('service', service)
    }
    // If only roles provided, filter by roles only
    else if (roles) {
      console.log('🔍 Filtering by roles only:', roles)
      if (Array.isArray(roles)) {
        query = query.in('role', roles)
      } else {
        query = query.eq('role', roles)
      }
    }
    // If no filters provided, return all users
    else {
      console.log('🔍 No filters provided, returning all users')
    }
    
    const { data, error } = await query
    
    console.log('🔍 getUsersByRoles result:', { 
      data: data?.length || 0, 
      error,
      filters: { roles, service },
      query: query.toSQL?.() || 'Query built'
    })
    
    if (error) {
      console.error('❌ getUsersByRoles error:', error)
    }
    
    return { data, error }
  } catch (err) {
    console.error('❌ getUsersByRoles exception:', err)
    return { data: null, error: err }
  }
}

export const getAllUsers = async () => {
  console.log('🔍 getAllUsers called')
  
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false })
    
    console.log('🔍 getAllUsers result:', { data, error })
    
    if (error) {
      console.error('❌ getAllUsers error:', error)
    }
    
    return { data, error }
  } catch (err) {
    console.error('❌ getAllUsers exception:', err)
    return { data: null, error: err }
  }
} 

// Debug function to test basic connection
export const testSupabaseConnection = async () => {
  console.log('🔍 Testing Supabase connection...')
  
  try {
    // Test 1: Basic connection
    const { data: testData, error: testError } = await supabase
      .from('users')
      .select('count')
      .limit(1)
    
    console.log('🔍 Basic connection test:', { testData, testError })
    
    // Test 2: Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser()
    console.log('🔍 Current user:', { user, userError })
    
    // Test 3: Check auth status
    const { data: { session }, error: sessionError } = await supabase.auth.getSession()
    console.log('🔍 Session:', { session, sessionError })
    
    return { testData, testError, user, userError, session, sessionError }
  } catch (err) {
    console.error('❌ Connection test exception:', err)
    return { error: err }
  }
}

// Additional helper functions for user management
export const getUserProfile = async (userId) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single()
  
  return { data, error }
}

export const updateUserProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from('users')
    .update(updates)
    .eq('id', userId)
  
  return { data, error }
}

export const getUserOrders = async (userId) => {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
  
  return { data, error }
}

export const createOrder = async (orderData) => {
  const { data, error } = await supabase
    .from('orders')
    .insert([orderData])
    .select()
  
  return { data, error }
}

export const updateOrder = async (orderId, updates) => {
  const { data, error } = await supabase
    .from('orders')
    .update(updates)
    .eq('id', orderId)
    .select()
  
  return { data, error }
}

export const getDeliveryPartners = async () => {
  const { data, error } = await supabase
    .from('delivery_partners')
    .select('*')
    .eq('status', 'active')
    .order('name')
  
  return { data, error }
}

export const deleteFile = async (bucketName, filePath) => {
  const { data, error } = await supabase.storage
    .from(bucketName)
    .remove([filePath])
  
  return { data, error }
}

export const listUserFiles = async (bucketName, userId) => {
  const { data, error } = await supabase.storage
    .from(bucketName)
    .list(`${userId}/`, {
      limit: 100,
      offset: 0
    })
  
  return { data, error }
}

export const searchUsers = async (searchTerm, service = 'logistics') => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('service', service)
    .or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,company_name.ilike.%${searchTerm}%`)
    .order('created_at', { ascending: false })
  
  return { data, error }
}

export const deleteUser = async (userId) => {
  const { data, error } = await supabase
    .from('users')
    .delete()
    .eq('id', userId)
  
  return { data, error }
}

export const updateUserStatus = async (userId, status) => {
  const { data, error } = await supabase
    .from('users')
    .update({ status })
    .eq('id', userId)
    .select()
  
  return { data, error }
}